import { useEffect, useMemo, type ReactNode } from 'react';
import { ConfigProvider, App as AntdApp, theme as antdTheme, type ThemeConfig } from 'antd';
import { useThemeStore, useThemeSync } from '@shared/lib/theme-store';

/**
 * Single source of truth for the app's look. Everything visual flows from here
 * through antd's `cssVar` tokens, so a change in this file reaches every screen
 * without touching individual components.
 *
 * Shadows are tinted toward the brand hue rather than pure black — neutral grey
 * shadows on a blue-tinted ground read as dirty, brand-tinted ones read as depth.
 */

const BRAND = {
  primary: '#2f5cf6',
  purple: '#8b5cf6',
  success: '#12a150',
  warning: '#e08700',
  error: '#e5484d',
};

const RADII = {
  borderRadius: 12,
  borderRadiusLG: 16,
  borderRadiusSM: 8,
  borderRadiusXS: 6,
};

const LIGHT_SHADOWS = {
  boxShadowTertiary: '0 1px 2px rgba(20, 29, 61, 0.05), 0 1px 3px rgba(20, 29, 61, 0.04)',
  boxShadowSecondary:
    '0 4px 14px -4px rgba(20, 29, 61, 0.12), 0 2px 6px -2px rgba(20, 29, 61, 0.06)',
  boxShadow: '0 16px 40px -14px rgba(20, 29, 61, 0.24), 0 4px 12px -6px rgba(20, 29, 61, 0.12)',
};

const DARK_SHADOWS = {
  boxShadowTertiary: '0 1px 2px rgba(0, 0, 0, 0.45)',
  boxShadowSecondary: '0 4px 16px -4px rgba(0, 0, 0, 0.55), 0 2px 6px -2px rgba(0, 0, 0, 0.4)',
  boxShadow: '0 20px 48px -16px rgba(0, 0, 0, 0.75), 0 6px 16px -8px rgba(0, 0, 0, 0.5)',
};

function buildTheme(dark: boolean): ThemeConfig {
  const shadows = dark ? DARK_SHADOWS : LIGHT_SHADOWS;

  return {
    algorithm: dark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    cssVar: true,
    hashed: false,
    token: {
      colorPrimary: BRAND.primary,
      colorInfo: BRAND.primary,
      colorSuccess: BRAND.success,
      colorWarning: BRAND.warning,
      colorError: BRAND.error,
      colorLink: BRAND.primary,

      // Slightly hue-biased grounds — a flat neutral reads as unfinished next to
      // the brand gradients used across the app.
      colorBgLayout: dark ? '#0B0F17' : '#F3F5FC',
      colorBgContainer: dark ? '#141A24' : '#FFFFFF',
      colorBgElevated: dark ? '#1A2130' : '#FFFFFF',
      colorBorder: dark ? '#2A3342' : '#DFE4F0',
      colorBorderSecondary: dark ? '#222A38' : '#EAEEF7',

      ...RADII,
      ...shadows,

      controlHeight: 38,
      fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
      fontSize: 14,
      lineHeight: 1.6,
      wireframe: false,
    },
    components: {
      Layout: {
        bodyBg: 'transparent',
        siderBg: dark ? '#111722' : '#FFFFFF',
        headerBg: 'transparent',
        headerHeight: 64,
        headerPadding: '0 24px',
      },
      Menu: {
        itemBorderRadius: 12,
        itemHeight: 44,
        itemMarginInline: 8,
        itemMarginBlock: 4,
        itemPaddingInline: 12,
        // The gradient fill for the active item is applied in globals.css —
        // a token only accepts a flat colour.
        itemSelectedColor: dark ? '#C7D4FF' : BRAND.primary,
        itemSelectedBg: 'transparent',
        itemHoverBg: dark ? 'rgba(255,255,255,0.05)' : 'rgba(47, 92, 246, 0.07)',
        iconSize: 17,
        collapsedIconSize: 18,
      },
      Button: {
        controlHeight: 38,
        borderRadius: 10,
        borderRadiusLG: 12,
        borderRadiusSM: 8,
        fontWeight: 500,
        primaryShadow: dark
          ? '0 6px 18px -6px rgba(47, 92, 246, 0.6)'
          : '0 6px 16px -6px rgba(47, 92, 246, 0.45)',
        defaultShadow: 'none',
        dangerShadow: 'none',
      },
      Card: {
        borderRadiusLG: 18,
        boxShadowTertiary: shadows.boxShadowTertiary,
        paddingLG: 20,
        headerFontSize: 15,
      },
      Input: { borderRadius: 10, controlHeight: 38, paddingInline: 12 },
      InputNumber: { borderRadius: 10, controlHeight: 38 },
      Select: { borderRadius: 10, controlHeight: 38, optionSelectedBg: dark ? '#1E2940' : '#EBF0FE' },
      DatePicker: { borderRadius: 10, controlHeight: 38 },
      Table: {
        borderRadiusLG: 16,
        headerBg: dark ? '#18202C' : '#F7F9FD',
        headerSplitColor: 'transparent',
        rowHoverBg: dark ? 'rgba(255,255,255,0.035)' : 'rgba(47, 92, 246, 0.045)',
        cellPaddingBlock: 14,
      },
      Tag: { borderRadiusSM: 7, defaultBg: dark ? '#1C2430' : '#F1F4FB' },
      Modal: { borderRadiusLG: 20, paddingContentHorizontalLG: 24 },
      Drawer: { borderRadiusLG: 20 },
      Segmented: { borderRadius: 10, itemSelectedBg: dark ? '#26324A' : '#FFFFFF' },
      Tabs: { horizontalItemPadding: '12px 4px', titleFontSize: 14 },
      Alert: { borderRadiusLG: 12 },
      Dropdown: { borderRadiusLG: 14, controlItemBgHover: dark ? '#222B3A' : '#F2F5FD' },
      Tooltip: { borderRadius: 8 },
      Badge: { textFontSizeSM: 11 },
      Progress: { defaultColor: BRAND.primary },
    },
  };
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  useThemeSync();
  const resolved = useThemeStore((s) => s.resolved);
  const dark = resolved === 'dark';

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('dark', dark);
    root.style.colorScheme = resolved;
  }, [dark, resolved]);

  const themeConfig = useMemo(() => buildTheme(dark), [dark]);

  return (
    <ConfigProvider theme={themeConfig}>
      <AntdApp>{children}</AntdApp>
    </ConfigProvider>
  );
}
